import AISparkIcon from "./AISparkIcon";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Navbar,
  Container,
  Nav,
  Dropdown,
  Modal,
  Button,
} from "react-bootstrap";
import {
  FaBars,
  FaBuilding,
  FaEnvelope,
  FaHeadset,
  FaHome,
  FaPhoneAlt,
  FaPrint,
  FaQuestionCircle,
  FaUser,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import {
  fetchPrintableTasks,
  isApplicationPacketPrintPath,
  printSelectedTaskPacket,
  resolveCurrentPrintableTaskId,
  resolveApplicationPacketUserId,
} from "./ApplicationPrintPacket";
import "../styles/TopNavBar.css";

function TopNavBar({ onToggleSidebar }) {
  const navigate = useNavigate();
  const helpToggleRef = useRef(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [helpAlign, setHelpAlign] = useState("start");
  const positionHelpMenu = () => {
    const rect = helpToggleRef.current?.getBoundingClientRect();
    if (rect) setHelpAlign(rect.left + 200 > window.innerWidth - 12 ? "end" : "start");
  };
  useEffect(() => {
    if (!helpOpen) return undefined;
    window.addEventListener("resize", positionHelpMenu);
    return () => window.removeEventListener("resize", positionHelpMenu);
  }, [helpOpen]);

  const role = localStorage.getItem("userRole");
  const user = JSON.parse(localStorage.getItem("user"));
  const loggedInUserId = localStorage.getItem("userId");
  const isAdmin = role === "1" || user?.role === "admin";

  const [showSupportModal, setShowSupportModal] = useState(false);
  const [showConfirmReturn, setShowConfirmReturn] = useState(false);
  const [isPreparingPrint, setIsPreparingPrint] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printTasks, setPrintTasks] = useState([]);
  const [selectedPrintTaskIds, setSelectedPrintTaskIds] = useState([]);
  const [printAllListRecords, setPrintAllListRecords] = useState(false);
  const [printProgress, setPrintProgress] = useState("");
  const [printUserId, setPrintUserId] = useState(null);

  const groupedPrintTasks = useMemo(() => {
    const groups = new Map();
    printTasks.forEach((task) => {
      const name = task.categoryName || "Other";
      if (!groups.has(name))
        groups.set(name, { name, order: task.categoryOrder, tasks: [] });
      groups.get(name).tasks.push(task);
    });
    return Array.from(groups.values())
      .sort((a, b) => a.order - b.order || a.name.localeCompare(b.name))
      .map((group) => ({
        ...group,
        tasks: group.tasks.sort(
          (a, b) =>
            a.taskOrder - b.taskOrder || a.t_title.localeCompare(b.t_title),
        ),
      }));
  }, [printTasks]);

  const handleHome = () => {
                const pathMatch =
                  window.location.pathname.match(/\/(?:[a-z-]+)\/(\d+)/);
                const viewedUserId = pathMatch?.[1];

                if (
                  isAdmin &&
                  viewedUserId &&
                  viewedUserId !== loggedInUserId
                ) {
                  setShowConfirmReturn(true);
                } else if (isAdmin) {
                  navigate("/admin?tab=users&filter=All&status=Associate");
                } else {
                  navigate("/userhome");
                }
              };

  const handleSupportOpen = () => setShowSupportModal(true);
  const handleSupportClose = () => setShowSupportModal(false);
  const handlePrint = async () => {
    if (isPreparingPrint) return;
    const targetUserId = resolveApplicationPacketUserId();
    if (!targetUserId) {
      window.print();
      return;
    }

    try {
      setIsPreparingPrint(true);
      setPrintProgress("Loading available tasks...");
      const tasks = await fetchPrintableTasks(targetUserId);
      if (!tasks.length && !isApplicationPacketPrintPath()) {
        window.print();
        return;
      }
      const currentTaskId = resolveCurrentPrintableTaskId(tasks);
      setPrintTasks(tasks);
      setPrintUserId(targetUserId);
      setSelectedPrintTaskIds(currentTaskId ? [Number(currentTaskId)] : []);
      setPrintAllListRecords(false);
      setShowPrintModal(true);
    } catch (error) {
      console.error("Task print selection failed:", error);
      window.alert(
        error.response?.data?.message ||
          "Failed to load the employee task list.",
      );
    } finally {
      setIsPreparingPrint(false);
      setPrintProgress("");
    }
  };

  const handlePrintSelectedTasks = async () => {
    const selectedTasks = printTasks.filter((task) =>
      selectedPrintTaskIds.includes(Number(task.t_id)),
    );
    if (!selectedTasks.length) {
      window.alert("Select at least one task to print.");
      return;
    }
    try {
      setIsPreparingPrint(true);
      setPrintProgress("Preparing selected tasks...");
      await printSelectedTaskPacket(printUserId, selectedTasks, {
        printAllListRecords,
        onProgress: setPrintProgress,
      });
      setShowPrintModal(false);
    } catch (error) {
      console.error("Selected task print failed:", error);
      window.alert(
        error.response?.data?.message ||
          error.message ||
          "Failed to prepare the task PDF.",
      );
    } finally {
      setIsPreparingPrint(false);
      setPrintProgress("");
    }
  };

  return (
    <>
      <Navbar
        variant="dark"
        fixed="top"
        className="top-nav-bar shadow-sm"
        onClickCapture={() =>
          window.dispatchEvent(new Event("close-left-slide-over"))
        }
      >
        <Container className="top-nav-container">
          <Nav className="d-flex gap-4">
            {isAdmin && onToggleSidebar && (
              <Nav.Link className="nav-icon nav-menu" onClick={onToggleSidebar}>
                <div className="icon">
                  <FaBars />
                </div>
                <div className="label">Menu</div>
              </Nav.Link>
            )}
            <Nav.Link
              onClick={handleHome}
              className="nav-icon nav-home"
            >
              <div className="icon">
                <FaHome />
              </div>
              <div className="label">Home</div>
            </Nav.Link>
            <Nav.Link onClick={handlePrint} className="nav-icon nav-print">
              <div className="icon">
                <FaPrint />
              </div>
              <div className="label">
                {isPreparingPrint ? "Preparing..." : "Print"}
              </div>
            </Nav.Link>
          </Nav>

          <button type="button" className="nav-company-home" data-no-standard-button="true" aria-label="Go to home page" onClick={handleHome}>
            <img src="/logoMain.png" alt="Company logo" height="36" />
          </button>

          <Nav className="d-flex gap-4">
            <Dropdown align={helpAlign} show={helpOpen} onToggle={(nextOpen) => {
              if (nextOpen) positionHelpMenu();
              setHelpOpen(nextOpen);
            }}>
              <Dropdown.Toggle ref={helpToggleRef} as={Nav.Link} className="nav-icon">
                <div className="icon">
                  <FaQuestionCircle />
                </div>
                <div className="label">Help</div>
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={handleSupportOpen}>
                  Support
                </Dropdown.Item>
                {Number(role ?? user?.role) === 1 && (
                  <Dropdown.Item className="ai-assistant-menu" onClick={() => window.dispatchEvent(new Event("open-admin-ai"))}>
                    <AISparkIcon /><span>HR Assistant</span>
                  </Dropdown.Item>
                )}
              </Dropdown.Menu>
            </Dropdown>

            <span id="announcement-nav-slot" className="nav-announcement-slot" />
            <Dropdown align="end">
              <Dropdown.Toggle as={Nav.Link} className="nav-icon">
                <div className="icon">
                  <FaUser />
                </div>
                <div className="label">My Account</div>
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item
                  onClick={() => {
                    navigate("/my-account");
                  }}
                >
                  My Account
                </Dropdown.Item>
                {localStorage.getItem("userRole") === "1" && (
                  <Dropdown.Item onClick={() => navigate("/admin?tab=users&filter=All&status=Associate")}>
                    Admin Page
                  </Dropdown.Item>
                )}
                <Dropdown.Item
                  onClick={() => {
                    localStorage.clear();
                    Object.keys(sessionStorage).filter((key) => key.startsWith("admin-ai:")).forEach((key) => sessionStorage.removeItem(key));
                    window.dispatchEvent(new Event("authSessionChanged"));
                    navigate("/");
                  }}
                >
                  Logout
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </Nav>
        </Container>
      </Navbar>

      <Modal
        show={showPrintModal}
        onHide={() => !isPreparingPrint && setShowPrintModal(false)}
        size="lg"
        centered
        scrollable
      >
        <Modal.Header closeButton={!isPreparingPrint}>
          <Modal.Title>Select Tasks to Print</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex flex-wrap gap-4 border-bottom pb-3 mb-3">
            <label className="d-flex align-items-center gap-2">
              <input
                type="checkbox"
                checked={
                  printTasks.length > 0 &&
                  selectedPrintTaskIds.length === printTasks.length
                }
                onChange={(event) =>
                  setSelectedPrintTaskIds(
                    event.target.checked
                      ? printTasks.map((task) => Number(task.t_id))
                      : [],
                  )
                }
                disabled={isPreparingPrint}
              />
              Select All
            </label>
            <label className="d-flex align-items-center gap-2">
              <input
                type="checkbox"
                checked={printAllListRecords}
                onChange={(event) =>
                  setPrintAllListRecords(event.target.checked)
                }
                disabled={isPreparingPrint}
              />
              Print all list records
            </label>
          </div>
          {groupedPrintTasks.map((group) => (
            <div key={group.name} className="mb-4">
              <h6 className="text-primary border-bottom pb-2">{group.name}</h6>
              <div className="row g-2">
                {group.tasks.map((task) => (
                  <div className="col-md-4" key={task.t_id}>
                    <label className="d-flex align-items-start gap-2 border-bottom py-2 h-100">
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={selectedPrintTaskIds.includes(
                          Number(task.t_id),
                        )}
                        onChange={(event) =>
                          setSelectedPrintTaskIds((current) =>
                            event.target.checked
                              ? [...new Set([...current, Number(task.t_id)])]
                              : current.filter(
                                  (id) => id !== Number(task.t_id),
                                ),
                          )
                        }
                        disabled={isPreparingPrint}
                      />
                      <span>{task.t_title}</span>
                    </label>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {isPreparingPrint && (
            <div className="position-sticky bottom-0 bg-white border-top py-3 text-center">
              <span
                className="spinner-border spinner-border-sm me-2"
                aria-hidden="true"
              />
              {printProgress || "Preparing PDF..."}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            disabled={isPreparingPrint}
            onClick={() => setShowPrintModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={isPreparingPrint || !selectedPrintTaskIds.length}
            onClick={handlePrintSelectedTasks}
          >
            {isPreparingPrint ? "Preparing..." : "Print Selected"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showSupportModal}
        onHide={handleSupportClose}
        centered
        dialogClassName="support-modal"
      >
        <Modal.Header closeButton className="support-modal-header">
          <div className="support-modal-heading">
            <span className="support-modal-heading-icon" aria-hidden="true">
              <FaQuestionCircle />
            </span>
            <div>
              <Modal.Title>Support Information</Modal.Title>
              <div className="support-modal-subtitle">
                Choose the right team for faster assistance.
              </div>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body className="support-modal-body">
          <section className="support-contact-card">
            <span className="support-contact-icon" aria-hidden="true">
              <FaBuilding />
            </span>
            <div className="support-contact-content">
              <h6>Application &amp; HR Support</h6>
              <p>Application questions and general assistance</p>
              <div className="support-contact-actions">
                <a href="tel:7083231740">
                  <FaPhoneAlt aria-hidden="true" />
                  <span>587-316-2101</span>
                </a>
                <a href="mailto:hr@newwaytrucking.com">
                  <FaEnvelope aria-hidden="true" />
                  <span>hr@newwaytrucking.com</span>
                </a>
              </div>
            </div>
          </section>

          <section className="support-contact-card">
            <span className="support-contact-icon" aria-hidden="true">
              <FaHeadset />
            </span>
            <div className="support-contact-content">
              <h6>Technical Support</h6>
              <p>Login issues, errors, and other technical problems</p>
              <div className="support-contact-actions">
                <a href="tel:15873556767">
                  <FaPhoneAlt aria-hidden="true" />
                  <span>587-355-6767</span>
                </a>
                <a href="mailto:it@newwaytrucking.com">
                  <FaEnvelope aria-hidden="true" />
                  <span>it@newwaytrucking.com</span>
                </a>
              </div>
            </div>
          </section>
        </Modal.Body>
        <Modal.Footer className="support-modal-footer">
          <Button className="support-modal-close" onClick={handleSupportClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showConfirmReturn}
        onHide={() => setShowConfirmReturn(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Return to Your Admin Page?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to return to admin page? You are currently
          viewing another employee profile.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowConfirmReturn(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setShowConfirmReturn(false);
              if (isAdmin) {
                navigate("/admin?tab=users&filter=All&status=Associate");
              } else {
                navigate("/userhome");
              }
            }}
          >
            Yes, Return
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default TopNavBar;
